import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslations } from "next-intl";
import { useRouter, useSearchParams } from "@/i18n/navigation";
import { PageHeader } from "@/components/page-header";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { RequestDetailContent } from "@/components/requests/request-detail-content";
import { RequestLinkedCases } from "@/components/requests/request-linked-cases";
import { PromoteRequestDialog } from "@/components/requests/promote-request-dialog";
import { useRequestDetail } from "@/hooks/use-request-detail";
import { toRomeDateInputValue } from "@/lib/format";

function RequestDetailSkeleton() {
  return (
    <div className="flex flex-1 flex-col gap-4 p-4">
      <Card>
        <CardHeader>
          <Skeleton className="h-5 w-40" />
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="space-y-1">
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-4 w-32" />
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

export const Route = createFileRoute("/_dashboard/requests/$id")({
  component: RequestDetailPage,
});

function RequestDetailPage() {
  const { id } = Route.useParams();
  const searchParams = useSearchParams();
  const typeHint = searchParams.get("type");
  const t = useTranslations("RequestDetail");
  const router = useRouter();
  const {
    data,
    type,
    requesterName,
    accident,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useRequestDetail(id, typeHint);
  const [promoteOpen, setPromoteOpen] = useState(false);

  if (isLoading) {
    return (
      <>
        <PageHeader
          title="..."
          parent={{ label: t("breadcrumbParent"), href: "/requests" }}
        />
        <RequestDetailSkeleton />
      </>
    );
  }

  if (isError) {
    return (
      <>
        <PageHeader
          title="—"
          parent={{ label: t("breadcrumbParent"), href: "/requests" }}
        />
        <div className="flex flex-1 flex-col items-center justify-center gap-3 p-4 text-center">
          <p className="text-muted-foreground">{t("loadError")}</p>
          <Button variant="outline" onClick={() => refetch()} disabled={isFetching}>
            {isFetching ? t("retrying") : t("retry")}
          </Button>
        </div>
      </>
    );
  }

  if (!data || !type) {
    return (
      <>
        <PageHeader
          title="—"
          parent={{ label: t("breadcrumbParent"), href: "/requests" }}
        />
        <div className="flex flex-1 items-center justify-center p-4">
          <p className="text-muted-foreground">{t("notFound")}</p>
        </div>
      </>
    );
  }

  const title = requesterName ?? t("breadcrumbParent");

  return (
    <>
      <PageHeader
        title={title}
        parent={{ label: t("breadcrumbParent"), href: "/requests" }}
      />
      <RequestDetailContent
        request={data}
        type={type}
        requesterName={requesterName}
        accident={accident}
        onPromote={() => setPromoteOpen(true)}
      />
      <div className="px-4 pb-4">
        <RequestLinkedCases requestId={id} />
      </div>
      <PromoteRequestDialog
        requestId={id}
        requestType={type}
        requestStatus={data.status}
        defaultReferenceDate={
          accident ? toRomeDateInputValue(accident.accident_time) : undefined
        }
        open={promoteOpen}
        onOpenChange={setPromoteOpen}
        onSuccess={(caseId) => {
          setPromoteOpen(false);
          router.push(`/cases/${caseId}`);
        }}
      />
    </>
  );
}
